import { PushPinSlash } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePins, useTogglePin } from "~/api/queries";
import { IconButton } from "~/foundation/components/IconButton";
import { Text } from "~/foundation/components/Text";

/**
 * W10 §3 — what this workspace put at the top of a collection.
 *
 * A pin is workspace-wide (`pin.TeamId`), not personal, so unpinning is offered only to someone
 * who may change the collection: the server checks nothing beyond the session
 * (`PinsController.Create` looks at `TeamId`/`UserId` and no policy at all — programme B4's
 * fault class), and a surface that let any reader rearrange everyone's landing row would be
 * honest about the wire and wrong about the product.
 *
 * Renders nothing at all when there are no pins — an empty "Pinned" heading above a collection
 * is noise on every collection that never uses the feature.
 */
export function PinnedRow({
  collectionId,
  canManage,
}: {
  collectionId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const { data } = usePins(collectionId);
  const togglePin = useTogglePin(collectionId);

  const documents = new Map((data?.documents ?? []).map((document) => [document.id, document]));
  const rows = (data?.pins ?? [])
    .map((pin) => {
      const document = documents.get(pin.documentId);
      return document ? { pinId: pin.id, document } : null;
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  if (rows.length === 0) {
    return null;
  }

  return (
    <section aria-label={t("Pinned")} className="flex flex-col gap-2">
      <Text variant="small" className="uppercase tracking-wide">
        {t("Pinned")}
      </Text>
      <ul className="grid gap-2 sm:grid-cols-2">
        {rows.map(({ pinId, document }) => (
          <li
            key={pinId}
            className="group relative flex items-center gap-2 rounded-md border border-divider px-3 py-2.5"
          >
            <span aria-hidden className="w-5 shrink-0 text-center">
              {document.emoji ?? "📌"}
            </span>
            <Link
              to="/documents/$documentId"
              params={{ documentId: document.id }}
              className="min-w-0 flex-1 truncate text-body text-text hover:text-accent"
            >
              {document.title}
            </Link>
            {canManage ? (
              <IconButton
                icon={PushPinSlash}
                size="sm"
                aria-label={t("Unpin “{{title}}”", { title: document.title })}
                disabled={togglePin.isPending}
                onClick={() => togglePin.mutate(document.id)}
                className="opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none"
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
